import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../../contexts/UserContext";

const LANGUAGES = [
  { code: "tr", label: "TR", flag: "🇹🇷" },
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "de", label: "DE", flag: "🇩🇪" },
  { code: "ar", label: "AR", flag: "🇸🇦" },
];

const CATEGORIES = [
  { key: "historical", icon: "ri-ancient-pavilion-line", slug: "historical" },
  { key: "food", icon: "ri-restaurant-line", slug: "food" },
  { key: "entertainment", icon: "ri-gamepad-line", slug: "entertainment" },
  { key: "accommodation", icon: "ri-hotel-line", slug: "accommodation" },
  { key: "nature", icon: "ri-plant-line", slug: "nature" },
  { key: "sightseeing", icon: "ri-eye-line", slug: "sightseeing" },
  { key: "featured", icon: "ri-star-line", slug: "featured" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Pages with light/white backgrounds — need solid white navbar with dark text
  // Applies to ALL users (logged in or not) on these pages
  const isLightPage =
    location.pathname === "/account" ||
    location.pathname.startsWith("/profile") ||
    location.pathname === "/search" ||
    location.pathname === "/login" ||
    location.pathname === "/register";

  // When not scrolled on a light page → white nav; when scrolled → dark; on hero pages → transparent
  const navBg = scrolled
    ? "bg-stone-900/97 backdrop-blur-md shadow-lg"
    : isLightPage
    ? "bg-white border-b border-stone-200"
    : "bg-transparent";

  const isDark = scrolled || !isLightPage;

  const linkCls = isDark
    ? "text-white/80 hover:text-white hover:bg-white/10"
    : "text-stone-600 hover:text-stone-900 hover:bg-stone-100";

  const borderCls = isDark
    ? "border-white/20 hover:border-white/40 text-white/80 hover:text-white"
    : "border-stone-300 hover:border-stone-500 text-stone-600 hover:text-stone-900";
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem("lang", code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    setProfileOpen(false);
    setMobileOpen(false);
    await logout();
    navigate("/");
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer group">
          <img
            src="https://storage.readdy-site.link/project_files/66944f43-98f8-4b93-9b7b-b30b3c21b463/e29c7939-3b82-4fee-869a-a2b8e38dffb4_Balksz-1.png?v=5e0579395dded2e25621d64762d27075"
            alt="BursaGuide360"
            className="h-8 w-auto object-contain"
            style={{ maxWidth: 160 }}
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-3.5 py-2 text-sm font-body transition-colors whitespace-nowrap cursor-pointer rounded-lg ${linkCls}`}
          >
            {t("nav_home")}
          </Link>

          {/* Categories Dropdown */}
          <div className="relative" ref={catRef}>
            <button
              onClick={() => { setCatOpen(!catOpen); setLangOpen(false); setProfileOpen(false); }}
              className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-body transition-colors whitespace-nowrap cursor-pointer rounded-lg ${linkCls}`}
            >
              {t("nav_categories")}
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-stone-100 overflow-hidden animate-fade-in">
                {CATEGORIES.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.slug}`}
                    onClick={() => setCatOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-anthracite-700 hover:bg-stone-50 hover:text-forest-700 transition-colors cursor-pointer group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-100 group-hover:bg-forest-100 transition-colors">
                      <i className={`${cat.icon} text-base text-stone-500 group-hover:text-forest-600`} />
                    </div>
                    <span className="font-medium">{t(`nav_cat_${cat.key}`)}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/routes"
            className={`px-3.5 py-2 text-sm font-body transition-colors whitespace-nowrap cursor-pointer rounded-lg ${linkCls}`}
          >
            {t("nav_routes")}
          </Link>
          <Link
            to="/map"
            className={`px-3.5 py-2 text-sm font-body transition-colors whitespace-nowrap cursor-pointer rounded-lg ${linkCls}`}
          >
            {t("nav_map")}
          </Link>
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-2">
          {/* User Search */}
          <Link
            to="/search"
            className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer ${linkCls}`}
            title="Kullanıcı Ara"
          >
            <i className="ri-user-search-line text-base" />
          </Link>

          {/* Language */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => { setLangOpen(!langOpen); setCatOpen(false); setProfileOpen(false); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-all cursor-pointer whitespace-nowrap ${borderCls}`}
            >
              <span>{currentLang.flag}</span>
              <span className="text-xs font-medium">{currentLang.label}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 w-36 bg-white rounded-xl shadow-xl border border-stone-100 overflow-hidden animate-fade-in">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left cursor-pointer transition-colors whitespace-nowrap ${
                      i18n.language === lang.code
                        ? "bg-forest-50 text-forest-700 font-semibold"
                        : "text-anthracite-600 hover:bg-stone-50"
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                    {i18n.language === lang.code && <i className="ri-check-line ml-auto text-forest-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Profile / Auth */}
          {user ? (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => { setProfileOpen(!profileOpen); setCatOpen(false); setLangOpen(false); }}
                className={`flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-xl border transition-all cursor-pointer group ${
                  isDark
                    ? "border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10"
                    : "border-stone-200 hover:border-stone-400 bg-stone-50 hover:bg-stone-100"
                }`}
              >
                {/* Avatar */}
                <div className="w-7 h-7 rounded-lg overflow-hidden flex items-center justify-center bg-emerald-700 flex-shrink-0">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-xs font-bold">{getInitials(user.displayName)}</span>
                  )}
                </div>
                {/* Name */}
                <span className={`text-sm font-medium max-w-[100px] truncate ${isDark ? "text-white/90" : "text-stone-700"}`}>
                  {user.displayName.split(" ")[0]}
                </span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${profileOpen ? "rotate-180" : ""} ${isDark ? "text-white/60" : "text-stone-400"}`} />
              </button>

              {/* Dropdown */}
              {profileOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-stone-100 overflow-hidden animate-fade-in">
                  {/* User info header */}
                  <div className="px-4 py-3 bg-stone-50 border-b border-stone-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-forest-600 flex-shrink-0">
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt={user.displayName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-white text-sm font-bold">{getInitials(user.displayName)}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-anthracite-800 truncate">{user.displayName}</p>
                        <p className="text-xs text-stone-500 truncate">{user.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="py-1.5">
                    <Link
                      to="/account"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-anthracite-700 hover:bg-stone-50 hover:text-forest-700 transition-colors cursor-pointer"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-dashboard-line text-base text-stone-400" />
                      </div>
                      <span>Hesabım</span>
                    </Link>
                    <Link
                      to={`/profile/${user.username}`}
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-anthracite-700 hover:bg-stone-50 hover:text-forest-700 transition-colors cursor-pointer"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-user-line text-base text-stone-400" />
                      </div>
                      <span>Profilim</span>
                    </Link>
                    <Link
                      to="/account"
                      onClick={() => setProfileOpen(false)}
                      state={{ tab: "routes" }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-anthracite-700 hover:bg-stone-50 hover:text-forest-700 transition-colors cursor-pointer"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-route-line text-base text-stone-400" />
                      </div>
                      <span>Rotalarım</span>
                    </Link>
                    <Link
                      to="/account"
                      onClick={() => setProfileOpen(false)}
                      state={{ tab: "venues" }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-anthracite-700 hover:bg-stone-50 hover:text-forest-700 transition-colors cursor-pointer"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-map-pin-line text-base text-stone-400" />
                      </div>
                      <span>Gittiklerim</span>
                    </Link>
                    <Link
                      to="/account"
                      onClick={() => setProfileOpen(false)}
                      state={{ tab: "coupons" }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-anthracite-700 hover:bg-stone-50 hover:text-forest-700 transition-colors cursor-pointer"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-coupon-line text-base text-stone-400" />
                      </div>
                      <span>Kuponlarım</span>
                    </Link>
                    {(user.role === 'business' || user.role === 'admin') && (
                      <Link
                        to="/business"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 transition-colors cursor-pointer"
                      >
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-store-2-line text-base text-amber-500" />
                        </div>
                        <span>İşletmem</span>
                      </Link>
                    )}
                    {user.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer"
                      >
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-shield-star-line text-base text-emerald-500" />
                        </div>
                        <span>Kontrol Paneli</span>
                      </Link>
                    )}
                  </div>

                  {/* Divider + Logout */}
                  <div className="border-t border-stone-100 py-1.5">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-logout-box-r-line text-base" />
                      </div>
                      <span>Çıkış Yap</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/account"
              className={`flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium border rounded-lg transition-all cursor-pointer whitespace-nowrap ${borderCls}`}
            >
              <i className="ri-user-line text-sm" />
              <span>Giriş Yap</span>
            </Link>
          )}

          {user && (user.role === 'business' || user.role === 'admin') && (
            <Link
              to="/business"
              className={`px-4 py-1.5 text-sm font-medium border rounded-lg transition-all cursor-pointer whitespace-nowrap ${borderCls}`}
            >
              {t("nav_for_business")}
            </Link>
          )}

          {user && user.role === 'admin' && (
            <Link
              to="/admin"
              className={`px-4 py-1.5 text-sm font-medium border rounded-lg transition-all cursor-pointer whitespace-nowrap ${borderCls}`}
            >
              Kontrol Paneli
            </Link>
          )}

          <Link
            to="/category/featured"
            className="px-4 py-1.5 text-sm font-semibold bg-forest-600 hover:bg-forest-500 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("nav_explore_btn")}
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className={`md:hidden p-2 cursor-pointer ${!isDark ? "text-stone-700" : "text-white"}`}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-3-line"} text-xl`} />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-anthracite-900 border-t border-white/10 px-4 py-4 flex flex-col gap-1 animate-fade-in">
          <Link to="/" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm text-white/80 hover:text-white rounded-lg hover:bg-white/10 cursor-pointer">
            {t("nav_home")}
          </Link>
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.key}
              to={`/category/${cat.slug}`}
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2.5 text-sm text-white/70 hover:text-white rounded-lg hover:bg-white/10 cursor-pointer flex items-center gap-2"
            >
              <i className={`${cat.icon} text-base`} />
              {t(`nav_cat_${cat.key}`)}
            </Link>
          ))}
          <Link to="/routes" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm text-white/80 hover:text-white rounded-lg hover:bg-white/10 cursor-pointer">
            {t("nav_routes")}
          </Link>
          <Link to="/map" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm text-white/80 hover:text-white rounded-lg hover:bg-white/10 cursor-pointer">
            {t("nav_map")}
          </Link>

          {/* Mobile: User section */}
          <div className="border-t border-white/10 pt-3 mt-2">
            {user ? (
              <div className="flex flex-col gap-1">
                {/* User info */}
                <div className="flex items-center gap-3 px-3 py-2 mb-1">
                  <div className="w-9 h-9 rounded-xl overflow-hidden flex items-center justify-center bg-forest-600 flex-shrink-0">
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user.displayName} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-sm font-bold">{getInitials(user.displayName)}</span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{user.displayName}</p>
                    <p className="text-xs text-white/50">{user.email}</p>
                  </div>
                </div>
                <Link to="/account" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm text-white/80 hover:text-white rounded-lg hover:bg-white/10 cursor-pointer">
                  <i className="ri-dashboard-line text-base" /> Hesabım
                </Link>
                <Link to={`/profile/${user.username}`} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm text-white/80 hover:text-white rounded-lg hover:bg-white/10 cursor-pointer">
                  <i className="ri-user-line text-base" /> Profilim
                </Link>
                {(user.role === 'business' || user.role === 'admin') && (
                  <Link to="/business" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm text-amber-300 hover:text-amber-200 rounded-lg hover:bg-white/10 cursor-pointer">
                    <i className="ri-store-2-line text-base" /> İşletmem
                  </Link>
                )}
                {user.role === 'admin' && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm text-emerald-300 hover:text-emerald-200 rounded-lg hover:bg-white/10 cursor-pointer">
                    <i className="ri-shield-star-line text-base" /> Kontrol Paneli
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 rounded-lg hover:bg-white/10 cursor-pointer w-full text-left whitespace-nowrap"
                >
                  <i className="ri-logout-box-r-line text-base" /> Çıkış Yap
                </button>
              </div>
            ) : (
              <Link to="/account" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm text-white/80 hover:text-white rounded-lg hover:bg-white/10 cursor-pointer">
                <i className="ri-user-line text-base" /> Giriş Yap / Kayıt Ol
              </Link>
            )}
          </div>

          <div className="border-t border-white/10 pt-3 mt-1 flex items-center gap-2 flex-wrap">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm cursor-pointer whitespace-nowrap ${
                  i18n.language === lang.code ? "bg-forest-600 text-white" : "bg-white/10 text-white/70"
                }`}
              >
                {lang.flag} {lang.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
